import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from '../utils/i18n-utils';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  PointOfSale as SalesIcon,
  Assessment as ReportsIcon,
  Logout as LogoutIcon,
  DirectionsCar as CarIcon,
  AccountBalance as AccountingIcon,
  Receipt as InvoiceIcon,
  MonetizationOn as ExpenseIcon,
  People as EmployeeIcon,
  ExpandLess,
  ExpandMore,
  Calculate as CalculateIcon,
  CurrencyExchange as CashManagementIcon,
  AdminPanelSettings as UserManagementIcon,
} from '@mui/icons-material';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 280;

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const [selectedPath, setSelectedPath] = React.useState(window.location.pathname);

  // State for menu collapse
  const [accountingOpen, setAccountingOpen] = React.useState(false);

  const handleAccountingClick = () => {
    setAccountingOpen(!accountingOpen);
  };

  React.useEffect(() => {
    setSelectedPath(window.location.pathname);
  }, [window.location.pathname]);

  const menuItems = [
    { text: t('navigation.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('navigation.inventory'), icon: <InventoryIcon />, path: '/dashboard/inventory' },
    { text: t('navigation.sales'), icon: <SalesIcon />, path: '/dashboard/sales' },
    { text: t('navigation.reports'), icon: <ReportsIcon />, path: '/dashboard/reports' },
    { text: t('navigation.carManagement'), icon: <CarIcon />, path: '/dashboard/car-management' },
    // Admin only menu item
    ...(isAdmin ? [{ text: t('navigation.userManagement') || 'User Management', icon: <UserManagementIcon />, path: '/dashboard/users' }] : []),
  ];

  const accountingMenuItems = [
    { text: t('navigation.accounting.dashboard'), icon: <DashboardIcon />, path: '/dashboard/accounting' },
    { text: t('navigation.accounting.expenses'), icon: <ExpenseIcon />, path: '/dashboard/accounting/expenses' },
    { text: t('navigation.accounting.expenseCategories') || 'Expense Categories', icon: <ExpenseIcon />, path: '/dashboard/accounting/expense-categories' },
    { text: t('navigation.accounting.invoices'), icon: <InvoiceIcon />, path: '/dashboard/accounting/invoices' },
    { text: t('navigation.accounting.employees'), icon: <EmployeeIcon />, path: '/dashboard/accounting/employees' },
    { text: t('navigation.accounting.cashManagement'), icon: <CashManagementIcon />, path: '/dashboard/accounting/cash-management' },
    { text: t('navigation.accounting.cashCount'), icon: <CalculateIcon />, path: '/dashboard/accounting/cash-count' },
  ];

  const handleLogout = () => {
    // TODO: Implement logout logic
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #101D68 0%, #2A3A8A 100%)',
          boxShadow: '0px 4px 20px rgba(16, 29, 104, 0.15)',
        }}
      >
        <Toolbar sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                fontWeight: 700,
                fontSize: '1.5rem',
                letterSpacing: '-0.02em',
              }}
            >
              {t('app.title')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageSwitcher />
            <IconButton 
              color="inherit" 
              onClick={handleLogout}
              sx={{
                ml: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        anchor={isRTL ? 'right' : 'left'}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: 'none',
            background: '#FFFFFF',
            boxShadow: '2px 0px 16px rgba(16, 29, 104, 0.08)',
          },
        }}
      >
        <Toolbar sx={{ 
          background: 'linear-gradient(135deg, #101D68 0%, #2A3A8A 100%)',
          minHeight: '64px !important',
        }} />
        <Box sx={{ 
          overflow: 'auto',
          pt: 2,
          pb: 2,
        }}>
          <List sx={{ px: 1.5 }}>
            {menuItems.map((item) => {
              const isSelected = selectedPath === item.path;
              return (
                <ListItem
                  key={item.text}
                  disablePadding
                  sx={{ 
                    mb: 0.5,
                    mx: 0.5,
                  }}
                >
                  <ListItemButton
                    onClick={() => {
                      navigate(item.path);
                      setSelectedPath(item.path);
                    }}
                    selected={isSelected}
                    sx={{ 
                      cursor: 'pointer',
                      textAlign: isRTL ? 'right' : 'left',
                      borderRadius: 2,
                      py: 1.25,
                      px: 2,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: isSelected ? 'rgba(16, 29, 104, 0.12)' : 'rgba(16, 29, 104, 0.06)',
                        transform: 'translateX(4px)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(16, 29, 104, 0.12)',
                        borderLeft: isRTL ? 'none' : '4px solid #101D68',
                        borderRight: isRTL ? '4px solid #101D68' : 'none',
                        '& .MuiListItemIcon-root': {
                          color: '#101D68',
                        },
                        '& .MuiListItemText-primary': {
                          color: '#101D68',
                          fontWeight: 600,
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 44,
                      mr: isRTL ? 'auto' : 2, 
                      ml: isRTL ? 2 : 'auto',
                      color: isSelected ? '#101D68' : '#6B7280',
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.9375rem',
                        fontWeight: isSelected ? 600 : 500,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
            
            {/* Accounting Menu Item with submenu */}
            <ListItem 
              onClick={handleAccountingClick}
              sx={{ 
                cursor: 'pointer',
                textAlign: isRTL ? 'right' : 'left',
                borderRadius: 2,
                mb: 0.5,
                mx: 0.5,
                py: 1.25,
                px: 2,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(16, 29, 104, 0.06)',
                },
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: 44,
                mr: isRTL ? 'auto' : 2, 
                ml: isRTL ? 2 : 'auto',
                color: '#6B7280',
              }}>
                <AccountingIcon />
              </ListItemIcon>
              <ListItemText 
                primary={t('navigation.accounting.title')}
                primaryTypographyProps={{
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                }}
              />
              {accountingOpen ? (
                <ExpandLess sx={{ color: '#6B7280' }} />
              ) : (
                <ExpandMore sx={{ color: '#6B7280' }} />
              )}
            </ListItem>
            
            {/* Accounting Submenu */}
            <Collapse in={accountingOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ px: 1.5 }}>
                {accountingMenuItems.map((item) => {
                  const isSelected = selectedPath === item.path;
                  return (
                    <ListItem
                      key={item.text}
                      disablePadding
                      sx={{ 
                        mb: 0.5,
                        mx: 0.5,
                      }}
                    >
                      <ListItemButton
                        onClick={() => {
                          navigate(item.path);
                          setSelectedPath(item.path);
                        }}
                        selected={isSelected}
                        sx={{ 
                          cursor: 'pointer',
                          textAlign: isRTL ? 'right' : 'left',
                          borderRadius: 2,
                          py: 1,
                          pl: isRTL ? 2 : 4,
                          pr: isRTL ? 4 : 2,
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: isSelected ? 'rgba(16, 29, 104, 0.12)' : 'rgba(16, 29, 104, 0.06)',
                            transform: 'translateX(4px)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(16, 29, 104, 0.12)',
                            borderLeft: isRTL ? 'none' : '3px solid #101D68',
                            borderRight: isRTL ? '3px solid #101D68' : 'none',
                            '& .MuiListItemIcon-root': {
                              color: '#101D68',
                            },
                            '& .MuiListItemText-primary': {
                              color: '#101D68',
                              fontWeight: 600,
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ 
                          minWidth: 36,
                          mr: isRTL ? 'auto' : 1.5, 
                          ml: isRTL ? 1.5 : 'auto',
                          color: isSelected ? '#101D68' : '#6B7280',
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: isSelected ? 600 : 500,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </List>
        </Box>
      </Drawer>
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: 4,
          marginLeft: isRTL ? 0 : undefined,
          marginRight: isRTL ? undefined : 0,
          background: '#F5F7FA',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Box sx={{ 
          maxWidth: '1400px',
          mx: 'auto',
        }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout; 